import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';

export interface IPhoneNumberInputProps {
  /**
   * Optional prop.Name attribute of the input element.
   * Pass it if you are using formik
   */
  name?: string;

  /**
   * The actual phone number value to input
   */
  value: string;

  /**
   * Optional prop to show default country on the phone input. If not provided will default to US.
   */
  defaultCountry?: string;

  /**
   * Bit flag that tells if the field should show an error
   * Usually it's when the input is invalid and field is touched but could be a combination
   * of anything depends on the consumer.
   */
  showErrorMessage: boolean;

  /**
   * Optional Bit flag that tells us if the field's background color should turn to pink
   * in case there is an error.
   */
  showErrorPinkBackground?: boolean;

  /**
   * Optional prop. If the input is not valid, what error message to show.
   * Default message that will be shown is "Invalid phone number provided"
   */
  errorMessage?: string;

  /**
   * Optional prop to show label on the input. Defaults to 'Phone'.
   */
  label?: string;

  /**
   * Callback function. Executes when the phone number value is changed.
   * @param value string value of the phone number as it changes
   * @returns
   */
  onChange: (value: string) => void;

  /**
   * Optional callback function. Executes when we click out of the input field.
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;

  /**
   * Optional Bit flag to disable the phone input. Useful if input is read only.
   */
  disabled?: boolean;

  /**
   * Optional prop that tells us if we can edit the country code. Defaults to false.
   */
  countryCodeEditable?: boolean;

  /**
   * Optional prop that makes the country dropdown disabled
   */
  disableCountryDropdown?: boolean;

  /**
   * Optional prop that shows the required asterik on the input
   */
  showAsterisk?: boolean;

  /**
   * Optional bit flag. If true will make the country list dropdown open upwards
   */
  openCountryListUpwards?: boolean;
}

export const useStyle = makeStyles(() => ({
  phoneInput: {
    '& .form-control': {
      width: '100%',
      borderRadius: '24px',
      height: '49px  !important',
    },
    '& .react-tel-input .country-list': {
      overflowY: 'auto', // Enable vertical scrollbar
      '&::-webkit-scrollbar': {
        width: '8px', // Width of the scrollbar
        background:'transparent !important'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#888', // Color of the scrollbar thumb
        borderRadius: '4px', // Rounded corners
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#555', // Color on hover
      },
    },
  },
  errorHelperText: { marginTop: '10px !important' },
}));

/**
 * Inosio Generic Phone Number Input. Keep this generic. Do not tightly couple with any consumer.
 * @param props
 * @returns
 */
export function PhoneNumberInput(props: IPhoneNumberInputProps) {
  const classes = useStyle();
  const [dropdownWidth, setDropdownWidth] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null); //used to calculate current width of the input element

  useEffect(() => {
    if (inputRef.current) {
      // Get the width of the input field
      const inputWidth = inputRef.current.offsetWidth;
      // Set the dropdown width to match the input field width
      setDropdownWidth(inputWidth);
    }
  }, [inputRef.current, props.value, window.innerWidth]); //if screen size changes fetch the width of the input field again to adjust the dropdown size
  return (
    <FormControl fullWidth className={classes.phoneInput}>
      <FormLabel sx={{ marginLeft: '15px', fontSize: '14px' }}>
        {props.label || 'Phone'} {props.showAsterisk && <span style={{ color: 'red' }}>*</span>}
      </FormLabel>
      <PhoneInput
        country={props.defaultCountry ?? 'us'}
        value={props.value}
        isValid={!props.showErrorMessage}
        onChange={props.onChange}
        disabled={props.disabled}
        onBlur={props.onBlur}
        countryCodeEditable={props.countryCodeEditable ?? false}
        disableDropdown={props.disableCountryDropdown}
        inputProps={{
          name: props.name,
          ref: inputRef, // Pass the inputRef to PhoneInput
        }}
        inputStyle={{
          backgroundColor: props.showErrorPinkBackground ? 'pink' : 'white',
          boxShadow: '0px 0px 40px 0px rgba(0, 0, 0, 0.1)',
        }}
        dropdownStyle={{
          width: dropdownWidth ? `${dropdownWidth}px` : 'auto', // Set the dropdown width dynamically
          margin: 0,
          borderRadius:'14px',
          top: props.openCountryListUpwards ? 'auto' : '',
          bottom: props.openCountryListUpwards ? '100%' : '', //position dropdown above the input
        }}
        specialLabel=""
      />
      {props.showErrorMessage && (
        <FormHelperText error className={classes.errorHelperText}>
          {props.errorMessage ?? 'Invalid phone number provided'}
        </FormHelperText>
      )}
    </FormControl>
  );
}
